import { useEffect } from "react";
import { useParams } from "react-router";
import { useStateUrlParams } from "@common/hooks/useStateUrlParams";
import { useRiskAreasContext } from "@common/providers/riskAreasProvider/RiskAreasProvider";
import { RiskAreaTab, riskAreaTabFromQuery, riskAreaTabToQuery } from "./RiskArea.types";
import { rememberRiskAreaTab } from "./RiskArea.utils";
import { RiskAreaAssessmentLevel } from "../../riskSettings/riskArea/riskAreaDialog/RiskAreaDialog.types";

const viewQueryKey = "view";

export function useRiskArea() {
    const { riskAreaId } = useParams() as { riskAreaId: string };
    const { riskAreas } = useRiskAreasContext();

    const [view, setView] = useStateUrlParams(viewQueryKey, riskAreaTabToQuery(RiskAreaTab.Operational));

    const selectedTab = riskAreaTabFromQuery(view);

    // Recorded before setView navigates: the nav menu builds its link to this area from the remembered view
    // and re-renders in the same commit as the navigation, so an effect-only write would leave its href one
    // navigation behind.
    const setSelectedTab = (tab: RiskAreaTab) => {
        rememberRiskAreaTab(riskAreaId, tab);
        setView(riskAreaTabToQuery(tab));
    };

    // Arriving on a view through a URL rather than the switcher counts as being there too.
    useEffect(() => {
        rememberRiskAreaTab(riskAreaId, selectedTab);
    }, [riskAreaId, selectedTab]);

    return {
        riskAreaName: riskAreas.find((x) => x.id === riskAreaId)?.name,
        selectedTab,
        setSelectedTab,
    };
}

// Which subjects the area assesses, from the areas menu already loaded for the navigation. Both are false until
// the areas have loaded, so callers hold back rather than flash the wrong tables.
export function useRiskAreaAssessmentLevel(riskAreaId: string | undefined) {
    const { riskAreas } = useRiskAreasContext();
    const assessmentLevel = riskAreas.find((x) => x.id === riskAreaId)?.assessmentLevel;

    return {
        assessesEntities: assessmentLevel === RiskAreaAssessmentLevel.Entity || assessmentLevel === RiskAreaAssessmentLevel.Both,
        assessesOrganisation: assessmentLevel === RiskAreaAssessmentLevel.Organisation || assessmentLevel === RiskAreaAssessmentLevel.Both,
    };
}
